/**
 * Filing the catalogue under its subjects.
 *
 * <p>Its own module rather than a helper inside the page, for the reason `absent.js`
 * and `bytes.js` are: the page imports React components and cannot be loaded to check
 * one function, and every edge here is a real row in somebody's catalogue. Subject has
 * free-text history in it — a book saved with `"  "` in the field is not the same shape
 * as one saved with null, and both have to land somewhere.
 */

/** Where a book with no subject goes. Named, not blank, so the heading reads. */
export const UNFILED = 'Unfiled';

/** The same, for a book with no class on it. */
export const UNCLASSED = 'No class';

/** What a book files under for one facet, with the blank case named rather than lost. */
const valueOf = (title, key, blank) => title?.[key]?.trim() || blank;

/**
 * Orders facet values, with the named-blank bucket last.
 *
 * <p>Numeric collation, because the classes are "Class 6" … "Class 10" and a plain
 * string sort puts Class 10 between Class 1 and Class 2.
 */
const byValue = (blank) => (a, b) => {
  if (a === b) return 0;
  if (a === blank) return 1;
  if (b === blank) return -1;
  return a.localeCompare(b, undefined, { numeric: true });
};

/**
 * One facet's chips: every value in the catalogue, each with how many books it would
 * leave and whether it is currently picked.
 *
 * @param visible the books the *other* facet's selection allows
 * @param all every book, so a value never disappears from the row it is in — a chip
 *   that vanishes as you filter cannot be unticked, and the list jumping about under
 *   the cursor is worse than a chip reading (0)
 */
function facet(visible, all, key, blank, chosen) {
  const counts = new Map();
  for (const title of visible) {
    const value = valueOf(title, key, blank);
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  const values = new Set(all.map((title) => valueOf(title, key, blank)));
  return [...values].sort(byValue(blank)).map((value) => ({
    value,
    count: counts.get(value) ?? 0,
    selected: chosen.has(value),
  }));
}

/**
 * The shelf as the screen renders it: the two facets' chips, and the books that
 * survive both.
 *
 * <p>Nothing is asked of the server. The whole catalogue is already in hand — it is
 * the list the page loaded — and a round trip per chip would make filtering feel
 * slower than scrolling past what it hides, the same reasoning `ResourceTypeFilter`
 * uses one screen over.
 *
 * <p>Each facet counts against the other's selection and never its own. Counting a
 * facet against itself is the mistake that makes faceted filters look broken: pick
 * Mathematics and every other subject reads (0), so the row says there are no Science
 * books when there are four of them one click away.
 *
 * @param selected {subjects, classes} — arrays of chosen values; empty means "all",
 *   which is not the same as listing them all and is why there is no All value
 */
export function shelfView(titles, selected = {}) {
  const all = titles ?? [];
  const subjects = new Set(selected.subjects ?? []);
  const classes = new Set(selected.classes ?? []);

  const bySubjectPick = (title) =>
    subjects.size === 0 || subjects.has(valueOf(title, 'subject', UNFILED));
  const byClassPick = (title) =>
    classes.size === 0 || classes.has(valueOf(title, 'classLevel', UNCLASSED));

  return {
    subjects: facet(all.filter(byClassPick), all, 'subject', UNFILED, subjects),
    classes: facet(all.filter(bySubjectPick), all, 'classLevel', UNCLASSED, classes),
    matches: all.filter((title) => bySubjectPick(title) && byClassPick(title)),
    total: all.length,
    filtered: subjects.size > 0 || classes.size > 0,
  };
}

/**
 * Adds or removes one value from a selection, as a chip toggle.
 *
 * <p>Returns a new array rather than mutating, because it feeds straight into
 * setState and React compares by identity.
 */
export function toggleValue(selection, value) {
  const current = selection ?? [];
  return current.includes(value)
    ? current.filter((entry) => entry !== value)
    : [...current, value];
}

/**
 * @param titles rows carrying a `subject`
 * @returns [subject, books][] — subjects alphabetical, Unfiled last, books in the
 *   order they arrived (the server sorts them by name)
 *
 * A book with no subject is filed under Unfiled rather than left off. The one screen
 * that loads content is the wrong place for a book to be invisible: an unfiled book is
 * usually a *new* book, which is exactly the one somebody is looking for.
 */
export function bySubject(titles) {
  const groups = new Map();
  for (const title of titles ?? []) {
    // Trimmed, so "Maths" and "Maths " are one heading and not two.
    const key = title.subject?.trim() || UNFILED;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(title);
  }
  return [...groups.entries()].sort(([a], [b]) => {
    if (a === b) return 0;
    if (a === UNFILED) return 1;
    if (b === UNFILED) return -1;
    return a.localeCompare(b);
  });
}
